import { useMutation } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { Content, ContentHeader } from 'components/v2/Content'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { CREATE_PASSENGER_TYPE } from 'features/PassengerTypes/mutations-legacy'
import { INewPassengerTypeForm } from 'features/PassengerTypes/components/NewPassengerType/types'
import { NewPassengerType } from 'features/PassengerTypes/components/NewPassengerType'
import { GTLanguage } from 'gql/types.generated'

const messages = defineMessages({
  helmet: {
    id: 'passengerTypes.new.helmet',
    defaultMessage: 'New Passenger Type | Nexbus Admin'
  }
})

const NewPassengerTypesView = (): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const login = useLogin()
  const [createPassengerType, { data, loading, error }] = useMutation(CREATE_PASSENGER_TYPE)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  useEffect(() => {
    if (data) {
      navigate('/passenger-types')
    }
  }, [data])

  const handleSubmit = (values: INewPassengerTypeForm): void => {
    createPassengerType({
      variables: {
        input: {
          name: values.passengerType,
          label: [
            {
              language: GTLanguage.Es,
              text: values.esLabel,
              plural: false
            },
            {
              language: GTLanguage.Es,
              text: values.esLabelPlural,
              plural: true
            },
            {
              language: GTLanguage.En,
              text: values.enLabel,
              plural: false
            },
            {
              language: GTLanguage.En,
              text: values.enLabelPlural,
              plural: true
            }
          ],
          abbreviation: [
            {
              language: GTLanguage.Es,
              text: values.esAbbreviation,
              plural: false
            },
            {
              language: GTLanguage.En,
              text: values.enAbbreviation,
              plural: false
            }
          ],
          description: values.enDescription
            ? [
                {
                  language: GTLanguage.Es,
                  text: values.esDescription,
                  plural: false
                },
                {
                  language: GTLanguage.En,
                  text: values.enDescription,
                  plural: false
                }
              ]
            : [],
          discount: Number(values.discount),
          countryCode: values.countryCode,
          salesChannelIDs: values.salesChannels.map(({ id }) => id)
        }
      }
    })
  }

  if (!canWrite(FEATURES.PASSENGER_TYPES, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PASSENGER_TYPES} />
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="passengerTypes.new.title" defaultMessage="Create passenger type" />
        </Heading.H3>
      </ContentHeader>

      <section>
        <Card.Container>
          <NewPassengerType onSubmit={handleSubmit} loading={loading} error={error} />
        </Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(NewPassengerTypesView)
